import { X } from "@/components/logos/X";
import { UserCircle, MapPin, Calendar, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMemo } from "react";
import TwitterMediaGrid from "@/components/twitter/TwitterMedia";
import {
  formatDate,
  formatNumber,
  processReplyData,
} from "@/components/twitter/twitterUtils";
import { TwitterAvatar } from "@/components/twitter/TwitterAvatar";
import type { Persona } from "@/api/personas";

const RepliedCard = ({
  reply,
  personaData,
}: {
  reply: any;
  personaData: Persona;
}) => {
  // Process the data with useMemo for performance
  const processedData = useMemo(() => processReplyData(reply), [reply]);

  // Safe rendering helper function
  const safeRender = (value: any) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  // Open tweet in new tab
  const openTweetInNewTab = () => {
    if (processedData?.tweetId && processedData.username) {
      window.open(
        `https://twitter.com/${processedData.username}/status/${processedData.tweetId}`,
        "_blank",
      );
    }
  };

  // Open response tweet in new tab
  const openResponseTweetInNewTab = () => {
    if (processedData?.responseTweetId) {
      window.open(
        `https://twitter.com/${processedData.username}/status/${processedData.responseTweetId}`,
        "_blank",
      );
    }
  };

  // Check if personaData is valid before rendering
  const validPersonaData =
    personaData && typeof personaData === "object" ? personaData : null;

  return (
    <div className="bg-background border rounded-2xl">
      {/* Green Badge and timestamp */}
      <div className="px-6 pt-4 pb-2 flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-green-500/10 text-green-500">
            Replied Message
          </Badge>
          <span className="text-sm text-muted-foreground">
            {processedData?.respondedAt
              ? formatDate(processedData.respondedAt)
              : "Unknown date"}
          </span>
        </div>
        <X width={16} height={16} fill="white" />
      </div>

      {/* Original Tweet Card */}
      <div className="border rounded-xl p-3 mx-6 bg-gray-100 text-black">
        <div className="flex items-start gap-2">
          {/* User Avatar */}
          <div className="flex-shrink-0">
            {processedData?.profileImage ? (
              <a
                href={
                  processedData.username
                    ? `https://twitter.com/${processedData.username}`
                    : "#"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="block hover:opacity-90 transition-opacity"
              >
                <img
                  src={processedData.profileImage}
                  alt={`${processedData.displayName || "User"}'s avatar`}
                  className="w-10 h-10 rounded-full"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.style.display = "none";

                    // Create fallback icon container
                    const div = document.createElement("div");
                    div.className =
                      "w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center";

                    // Create icon inside container
                    const icon = document.createElement("div");
                    icon.innerHTML =
                      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-gray-500"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>';
                    div.appendChild(icon);

                    // Replace img with div
                    e.currentTarget.parentNode?.replaceChild(
                      div,
                      e.currentTarget,
                    );
                  }}
                />
              </a>
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <UserCircle className="w-5 h-5 text-gray-500" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            {/* User Info Header */}
            <div className="flex flex-col">
              <div className="flex items-start justify-between">
                <div className="flex flex-col">
                  <div className="font-bold text-black line-clamp-1">
                    {processedData?.displayName || "Unknown User"}
                    {processedData?.verified && (
                      <span className="ml-1 inline-flex items-center justify-center bg-blue-500 text-white rounded-full w-4 h-4 text-sm">
                        ✓
                      </span>
                    )}
                  </div>
                  <div className="text-gray-500 text-sm">
                    @{processedData?.username || "unknown"}
                  </div>
                </div>
                <span className="text-sm text-gray-500 flex-shrink-0">
                  {processedData?.timestamp
                    ? formatDate(processedData.timestamp)
                    : "Unknown date"}
                </span>
              </div>

              {/* User meta information */}
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500 mt-1 mb-2">
                {processedData?.location && (
                  <div className="flex items-center">
                    <MapPin className="w-3 h-3 mr-1" />
                    <span>{processedData.location}</span>
                  </div>
                )}
                {processedData?.joinedDate && (
                  <div className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    <span>
                      Joined{" "}
                      {formatDate(processedData.joinedDate, {
                        month: "long",
                        year: "numeric",
                        day: undefined,
                        hour: undefined,
                        minute: undefined,
                      })}
                    </span>
                  </div>
                )}
                {typeof processedData?.followersCount === "number" && (
                  <div className="flex items-center">
                    <Users className="w-3 h-3 mr-1" />
                    <span>
                      {formatNumber(processedData.followersCount)} followers
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Tweet content */}
            <p className="mt-1 text-black text-sm">
              {safeRender(processedData?.tweetText) || "No message content"}
            </p>
          </div>
        </div>

        {/* Using our reusable media grid component */}
        {processedData?.mediaUrls && processedData.mediaUrls.length > 0 && (
          <TwitterMediaGrid urls={processedData.mediaUrls} />
        )}

        {/* Thread context */}
        {processedData?.threadContext &&
          Array.isArray(processedData.threadContext) &&
          processedData.threadContext.length > 1 && (
            <div className="mt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={openTweetInNewTab}
                className="text-gray-600 hover:text-white hover:bg-gray-800"
              >
                Show full conversation in new tab
              </Button>
            </div>
          )}
      </div>

      {/* Replying to indicator - Simple text only, as shown in the screenshot */}
      <div className="px-6 pt-4 pb-2">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          {validPersonaData ? (
            <TwitterAvatar persona={validPersonaData} />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
              <UserCircle className="w-4 h-4 text-gray-500" />
            </div>
          )}
          <div className="text-right">
            Replying to @{processedData?.username || "unknown"}
          </div>
        </div>
      </div>

      {/* Response content - Direct text without any card or border */}
      <div className="px-6 py-2">
        <p className="text-sm">
          {safeRender(processedData?.responseText) ||
            "No response text available"}
        </p>
      </div>

      {/* Footer with timestamp and view button */}
      <div className="px-6 py-3 flex justify-between items-center border-t border-border mt-2">
        <span className="text-sm text-muted-foreground">
          Replied on{" "}
          {processedData?.respondedAt
            ? formatDate(processedData.respondedAt)
            : "Unknown date"}
        </span>

        <Button
          variant="outline"
          size="sm"
          onClick={openResponseTweetInNewTab}
          disabled={!processedData?.responseTweetId}
        >
          VIEW ON TWITTER
        </Button>
      </div>
    </div>
  );
};

export default RepliedCard;
